import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";

// PMF masthead — single source of truth for the wordmark, shared by
// PublicReport.jsx and SkipperDashboard.jsx.
//   • Desktop: large centered wordmark lockup (auto-fits to one line) + subtitle,
//     with the corner menu/actions pinned top-right on the wordmark line.
//   • Mobile: slim sticky bar — small wordmark left, corner menu right.
//
// Wordmark: "Pardon My French" in Plus Jakarta Sans extrabold (navy) + a smaller
// red superscript "Racing" and three tapering red speed streaks.

const MAX_WORDMARK = 40; // px — desktop cap (Jakarta extrabold)
const MIN_WORDMARK = 18; // px — never smaller than this
const RACE_RED = "var(--race-red)";

function useIsMobile() {
  const [m, setM] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 600 : false
  );
  useEffect(() => {
    const onResize = () => setM(window.innerWidth <= 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return m;
}

function CornerActions({ actions, isMobile }) {
  if (!actions || actions.length === 0) return null;
  const link = {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: isMobile ? 10 : 11,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "var(--slate)",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 600,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  };
  const round = {
    width: 32, height: 32, borderRadius: "50%",
    border: "1px solid var(--border)", background: "var(--surface)",
    color: "var(--slate)", cursor: "pointer", fontSize: 18, lineHeight: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  };
  return (
    <div style={{ display: "flex", gap: isMobile ? 12 : 18, alignItems: "center" }}>
      {actions.map((a, i) =>
        a.round ? (
          <button key={i} type="button" aria-label={a.ariaLabel || a.label} onClick={a.onClick} style={round}>{a.label}</button>
        ) : a.href ? (
          <a key={i} href={a.href} style={link}>{a.label}</a>
        ) : (
          <button key={i} type="button" onClick={a.onClick} style={link}>{a.label}</button>
        )
      )}
    </div>
  );
}

export default function PmfMasthead({
  subtitle = "",
  note = null,
  cornerActions = [],
  cornerSlot = null,
  center = null,
  isMobile: isMobileProp,
  compact = false,
}) {
  const auto = useIsMobile();
  const isMobile = isMobileProp != null ? isMobileProp : auto;

  const lineRef = useRef(null);

  // Scale the wordmark line to fill the available width on one line, capped.
  const fit = useCallback(() => {
    const line = lineRef.current;
    if (!line || !line.parentElement) return;
    const avail = line.parentElement.clientWidth;
    if (!avail) return;
    line.style.fontSize = MAX_WORDMARK + "px";
    const natural = line.scrollWidth;
    let size = MAX_WORDMARK;
    if (natural > avail && natural > 0) {
      size = Math.max(MIN_WORDMARK, Math.floor((MAX_WORDMARK * avail) / natural));
    }
    line.style.fontSize = size + "px";
  }, []);

  useLayoutEffect(() => {
    fit();
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && lineRef.current && lineRef.current.parentElement) {
      ro = new ResizeObserver(() => fit());
      ro.observe(lineRef.current.parentElement);
    }
    window.addEventListener("resize", fit);
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fit).catch(() => {});
    }
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [fit]);

  // ── Mobile: slim bar (small wordmark left, menu right) — scrolls with content ──
  if (isMobile) {
    return (
      <header style={{ background: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", gap: 12, padding: "11px 16px" }}>
          <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, letterSpacing: "-0.02em", color: "var(--text)", fontSize: 22, lineHeight: 1, whiteSpace: "nowrap", flexShrink: 0 }}>
            Pardon My French<span style={{ fontWeight: 700, fontSize: "0.4em", color: RACE_RED, verticalAlign: "super", marginLeft: "0.05em" }}>Racing</span>
            <span aria-hidden="true" style={{ display: "inline-flex", flexDirection: "column", gap: 2, marginLeft: 7, verticalAlign: "middle" }}>
              <span style={{ height: 2, width: 16, background: RACE_RED, borderRadius: 2 }} />
              <span style={{ height: 2, width: 11, background: RACE_RED, borderRadius: 2, opacity: 0.7 }} />
              <span style={{ height: 2, width: 7, background: RACE_RED, borderRadius: 2, opacity: 0.45 }} />
            </span>
          </div>
          <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>{center}</div>
          <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            {cornerSlot || <CornerActions actions={cornerActions} isMobile />}
          </div>
        </div>
      </header>
    );
  }

  // ── Desktop compact: the Feed's cinematic hero carries the wordmark, so the
  // masthead here is just a slim bar that keeps the corner menu reachable
  // (avoids showing the wordmark twice on one screen). ──
  if (compact) {
    return (
      <header style={{ background: "var(--bg)", position: "relative" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "16px 24px 0", display: "flex", justifyContent: "flex-end", alignItems: "center", minHeight: 22 }}>
          {(cornerSlot || cornerActions.length > 0) && (cornerSlot || <CornerActions actions={cornerActions} isMobile={isMobile} />)}
        </div>
      </header>
    );
  }

  // ── Desktop: centered lockup ──
  return (
    <header style={{ background: "var(--bg)", position: "relative" }}>
      <div
        style={{
          position: "relative",
          maxWidth: 900,
          margin: "0 auto",
          padding: isMobile ? "16px 14px 0" : "28px 24px 0",
          textAlign: "center",
        }}
      >
        {(cornerSlot || cornerActions.length > 0) && (
          <div style={{ position: "absolute", top: 42, right: 24, zIndex: 40 }}>
            {cornerSlot || <CornerActions actions={cornerActions} isMobile={isMobile} />}
          </div>
        )}

        {/* Wordmark line — auto-fit, one line, never wraps */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 0 }}>
          <h1
            ref={lineRef}
            style={{
              margin: 0,
              display: "inline-block",
              whiteSpace: "nowrap",
              lineHeight: 1.04,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "var(--text)",
              fontSize: isMobile ? 24 : 40,
            }}
          >
            Pardon My French<span style={{ fontWeight: 700, fontSize: "0.4em", color: RACE_RED, verticalAlign: "super", marginLeft: "0.05em" }}>Racing</span><span aria-hidden="true" style={{ display: "inline-flex", flexDirection: "column", gap: "0.05em", marginLeft: "0.22em", verticalAlign: "middle" }}>
              <span style={{ height: "0.06em", width: "0.62em", background: RACE_RED, borderRadius: 2 }} />
              <span style={{ height: "0.06em", width: "0.42em", background: RACE_RED, borderRadius: 2, opacity: 0.7 }} />
              <span style={{ height: "0.06em", width: "0.24em", background: RACE_RED, borderRadius: 2, opacity: 0.45 }} />
            </span>
          </h1>
        </div>

        {subtitle && (
          <div
            style={{
              marginTop: isMobile ? 8 : 9,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: isMobile ? 10 : 10,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--slate)",
            }}
          >
            {subtitle}
          </div>
        )}

        {note && (
          <div
            style={{
              marginTop: 10,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12,
              color: "var(--gold-lt)",
            }}
          >
            {note}
          </div>
        )}
      </div>
    </header>
  );
}
